import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { GlobalSet } from "../../global/GlobalProvider";
import { useTranslation } from "react-i18next";

const API_KEY = "eyJ0eXAiOiJKV1Qidfg%!#%$%LCdf#*&*JhbGciOiJSUzI1NiJ9";
const INITIAL_PAGINATION = { current_page: 1, last_page: 1, per_page: 20, total: 0 };

const Icon = ({ name, size = 19 }) => {
  const paths = {
    wallet: <><rect x="3" y="6" width="18" height="15" rx="3"/><path d="M3 10h18"/><path d="M16 15h2"/><path d="M7 6V4a2 2 0 0 1 2-2h9"/></>,
    refresh: <><path d="M20 11a8 8 0 0 0-14-5L4 8"/><path d="M4 3v5h5"/><path d="M4 13a8 8 0 0 0 14 5l2-2"/><path d="M20 21v-5h-5"/></>,
    chart: <><path d="M3 3v18h18"/><path d="m7 15 4-5 4 3 5-7"/></>,
    calendar: <><rect x="3" y="5" width="18" height="16" rx="2"/><path d="M7 3v4M17 3v4M3 10h18"/></>,
    coins: <><ellipse cx="12" cy="6" rx="8" ry="3"/><path d="M4 6v6c0 2 4 3 8 3s8-1 8-3V6M4 12v6c0 2 4 3 8 3s8-1 8-3v-6"/></>,
    gift: <><rect x="3" y="9" width="18" height="12" rx="2"/><path d="M12 9v12M3 13h18M12 9C6 9 5 3 9 3c2 0 3 3 3 6Zm0 0c6 0 7-6 3-6-2 0-3 3-3 6Z"/></>,
    medal: <><circle cx="12" cy="14" r="5"/><path d="m7 2 5 7 5-7M7 2h10M12 12v4"/></>,
    crown: <><path d="m2 8 5 4 5-7 5 7 5-4-2 12H4L2 8Z"/></>,
    chevron: <path d="m9 18 6-6-6-6"/>,
    search: <><circle cx="11" cy="11" r="7"/><path d="m16 16 5 5"/></>,
    layers: <><path d="m12 2 9 5-9 5-9-5 9-5Z"/><path d="m3 12 9 5 9-5M3 17l9 5 9-5"/></>,
  };
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{paths[name] || paths.chart}</svg>;
};

const getIncomeIcon = (type) => {
  if (type === "BRISE Earning" || type === "BRISE Genaration") return "coins";
  const value = String(type || "").toLowerCase();
  if (value.includes("refer")) return "gift";
  if (value.includes("salary")) return "medal";
  if (value.includes("bonus")) return "crown";
  return "chart";
};

const BasicDatatable = () => {
  const { t } = useTranslation();
  const { CurrencySymbol } = GlobalSet();
  const [income, setIncome] = useState([]);
  const [pagination, setPagination] = useState(INITIAL_PAGINATION);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [error, setError] = useState("");
  const requestId = useRef(0);
  const mounted = useRef(true);

  const fetchIncome = useCallback(async (page = 1) => {
    if (!localStorage.getItem("auth_token")) return;
    const currentRequest = ++requestId.current;
    setLoading(true);
    setError("");
    try {
      const res = await axios.post(`/apps-income-history?page=${page}`, {
        key_s: API_KEY,
        currency: localStorage.getItem("selectedCurrency"),
      });
      if (!mounted.current || currentRequest !== requestId.current) return;
      if (res.data.status === "success") {
        setIncome(Array.isArray(res.data.data) ? res.data.data : []);
        setPagination({ ...INITIAL_PAGINATION, ...(res.data.pagination || {}) });
      } else {
        setError(res.data.message || "Unable to load income history.");
      }
    } catch (err) {
      if (mounted.current && currentRequest === requestId.current) {
        setError("Unable to load income history. Please try again.");
      }
    } finally {
      if (mounted.current && currentRequest === requestId.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    mounted.current = true;
    fetchIncome(1);
    return () => { mounted.current = false; requestId.current += 1; };
  }, [fetchIncome]);

  const page = Math.max(1, Number(pagination.current_page) || 1);
  const lastPage = Math.max(1, Number(pagination.last_page) || 1);
  const perPage = Math.max(1, Number(pagination.per_page) || 20);
  const total = Math.max(0, Number(pagination.total) || 0);
  const pages = useMemo(() => {
    const start = Math.max(1, Math.min(page - 2, lastPage - 4));
    return Array.from({ length: Math.min(5, lastPage - start + 1) }, (_, i) => start + i);
  }, [page, lastPage]);
  const filteredIncome = useMemo(() => {
    const term = search.trim().toLowerCase();
    return term ? income.filter(row => [row.income_Type, row.income_Amount, row.date].some(v => String(v ?? "").toLowerCase().includes(term))) : income;
  }, [income, search]);
  const amountPrefix = type => (type === "BRISE Earning" || type === "BRISE Genaration") ? "BRISE " : (CurrencySymbol || "");
  const goPage = next => { if (!loading && next >= 1 && next <= lastPage && next !== page) { setSearch(""); fetchIncome(next); } };

  return (
    <>
      <style>{`
        html,body,#root{background:#00191D!important;min-height:100%;}
        body{color:#fff!important}
        .rmih,.rmih *{box-sizing:border-box}
        .rmih{--cyan:#16B8C7;--muted:#a4bdc0;--line:rgba(78,201,213,.2);font:400 14px/1.55 inherit;color:#fff;background:#00191D;min-height:100vh;padding:16px 4px 35px;width:100%}
        .rmih-shell{width:100%;max-width:1440px;margin:0 auto}
        .rmih-hero{display:flex;align-items:center;justify-content:space-between;gap:14px;padding:21px;border:1px solid var(--line);border-radius:19px;background:linear-gradient(110deg,#073039,#00191D 75%);margin-bottom:14px;animation:rmih-enter .32s ease-out both}
        .rmih-hero-main{display:flex;align-items:center;gap:13px;min-width:0}
        .rmih-hero-icon{width:47px;height:47px;display:grid;place-items:center;flex:none;border-radius:14px;color:#00191D;background:linear-gradient(135deg,#63e1e9,#16B8C7,#098694)}
        .rmih h2,.rmih h3,.rmih p{margin:0}
        .rmih h2{font-size:22px;font-weight:800;color:#fff;line-height:1.25}
        .rmih-sub{color:var(--muted);margin-top:4px!important}
        .rmih-btn{display:inline-flex;align-items:center;justify-content:center;gap:7px;cursor:pointer;border-radius:11px;border:1px solid var(--line);padding:10px 15px;background:#092d33;color:#fff;font-size:14px;font-weight:700;transition:transform .18s,border-color .18s,background .18s}
        .rmih-btn:hover:not(:disabled){transform:translateY(-2px);border-color:#16B8C7}
        .rmih-btn:disabled{opacity:.5;cursor:not-allowed}
        .rmih-btn-primary{background:linear-gradient(115deg,#4bd7e1,#16B8C7,#0a91a1);border-color:transparent;color:#00191D}
        .rmih-stats{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:12px;margin-bottom:14px}
        .rmih-stat{padding:18px;border:1px solid var(--line);border-radius:16px;background:#06272d;min-width:0;animation:rmih-enter .35s ease-out both}
        .rmih-stat:nth-child(2){background:#fff;color:#09282e;border-color:#fff}
        .rmih-stat-head{display:flex;justify-content:space-between;align-items:center;gap:8px;margin-bottom:12px}
        .rmih-stat-label{font-size:13px;color:var(--muted);font-weight:600}
        .rmih-stat:nth-child(2) .rmih-stat-label{color:#52717a}
        .rmih-stat-icon{display:grid;place-items:center;width:33px;height:33px;border-radius:10px;background:rgba(22,184,199,.14);color:#44d8e3}
        .rmih-stat:nth-child(2) .rmih-stat-icon{background:#def7f9;color:#008a99}
        .rmih-stat-value{font-size:25px;line-height:1.2;font-weight:800;overflow-wrap:anywhere}
        .rmih-stat-foot{font-size:12px;color:var(--muted);margin-top:5px!important}
        .rmih-stat:nth-child(2) .rmih-stat-foot{color:#52717a}
        .rmih-panel{border:1px solid var(--line);border-radius:18px;background:#05252b;overflow:hidden;animation:rmih-enter .4s ease-out both}
        .rmih-panel-head{padding:17px 18px;display:flex;align-items:center;justify-content:space-between;flex-wrap:wrap;gap:12px;border-bottom:1px solid var(--line)}
        .rmih-panel-title{display:flex;align-items:center;gap:9px;font-weight:800;font-size:16px;color:#fff}
        .rmih-panel-title svg{color:#39cdd9}
        .rmih-tools{display:flex;align-items:center;gap:9px;flex-wrap:wrap}
        .rmih-search{display:flex;align-items:center;gap:8px;border:1px solid var(--line);border-radius:10px;background:#00191D;color:#8db4ba;padding:0 11px;min-width:220px;height:40px}
        .rmih-search input{background:transparent;border:0;outline:0;color:#fff;width:100%;font-size:14px;min-width:0}
        .rmih-search input::placeholder{color:#83a7ac}
        .rmih-page-label{padding:7px 11px;background:rgba(22,184,199,.11);border:1px solid var(--line);border-radius:9px;color:#63dbe4;font-weight:700}
        .rmih-scroll{overflow-x:auto;width:100%;-webkit-overflow-scrolling:touch}
        .rmih-table{width:100%;border-collapse:collapse;min-width:650px;text-align:left;margin:0;background:transparent}
        .rmih-table thead{background:#0a353c}
        .rmih-table th{color:#a9d9de;font-size:12px;letter-spacing:.03em;font-weight:800;padding:14px 18px;white-space:nowrap;border-bottom:1px solid var(--line)}
        .rmih-table td{padding:15px 18px;color:#fff;border-bottom:1px solid rgba(78,201,213,.11);vertical-align:middle;font-size:14px}
        .rmih-table tbody tr{transition:background .15s}
        .rmih-table tbody tr:hover{background:rgba(22,184,199,.065)}
        .rmih-table tbody tr:last-child td{border-bottom:0}
        .rmih-type{display:inline-flex;align-items:center;gap:10px;font-weight:700;color:#f3fcfc}
        .rmih-type-icon{display:grid;place-items:center;width:32px;height:32px;border-radius:10px;background:rgba(22,184,199,.13);color:#47d7e2;flex:none}
        .rmih-amount{display:inline-block;border-radius:9px;padding:7px 12px;background:rgba(22,184,199,.13);color:#69e3eb;font-weight:800;white-space:nowrap}
        .rmih-date{color:#b7cbce}
        .rmih-empty{text-align:center;padding:44px 12px;color:var(--muted)}
        .rmih-empty svg{display:block;margin:0 auto 12px;color:#3ac7d3}
        .rmih-error{margin:12px 18px 0;padding:11px 13px;border-radius:10px;border:1px solid rgba(255,125,125,.3);background:rgba(255,90,90,.08);color:#ffc0c0}
        .rmih-footer{padding:15px 18px;display:flex;justify-content:space-between;align-items:center;gap:14px;flex-wrap:wrap;border-top:1px solid var(--line);background:#042027}
        .rmih-count{color:var(--muted);font-size:13px}
        .rmih-pages{display:flex;gap:5px;align-items:center;flex-wrap:wrap}
        .rmih-page{min-width:35px;height:35px;display:grid;place-items:center;padding:0 9px;border-radius:9px;background:#0b333a;color:#d4f1f3;border:1px solid var(--line);cursor:pointer;font-size:13px;font-weight:700;transition:background .15s}
        .rmih-page:hover:not(:disabled){background:#12515b}
        .rmih-page.active{background:linear-gradient(120deg,#54d8e2,#16B8C7);color:#00191D;border-color:transparent}
        .rmih-page:disabled{opacity:.4;cursor:not-allowed}
        @keyframes rmih-enter{from{opacity:.4;transform:translateY(7px)}to{opacity:1;transform:translateY(0)}}
        @media(max-width:700px){.rmih{padding-top:9px}.rmih-hero{padding:15px}.rmih h2{font-size:18px}.rmih-stats{grid-template-columns:repeat(2,minmax(0,1fr));gap:8px}.rmih-stat{padding:13px}.rmih-stat:last-child{grid-column:1/-1}.rmih-stat-value{font-size:20px}.rmih-panel-head{padding:13px}.rmih-tools,.rmih-search{width:100%}.rmih-footer{padding:12px;justify-content:center}.rmih-count{text-align:center}.rmih-page-label{display:none}}
        @media(max-width:410px){.rmih-hero{align-items:flex-start;flex-direction:column}.rmih-hero .rmih-btn{width:100%}.rmih-stat-value{font-size:18px}}
        @media(prefers-reduced-motion:reduce){.rmih *{animation:none!important;transition:none!important}}
      `}</style>
      <main className="rmih">
        <div className="rmih-shell">
          <header className="rmih-hero">
            <div className="rmih-hero-main">
              <span className="rmih-hero-icon"><Icon name="wallet" size={24}/></span>
              <div><h2>{t("pro_IncHis_inc_his")}</h2><p className="rmih-sub">Review your income transactions and earning activity</p></div>
            </div>
            <button type="button" className="rmih-btn rmih-btn-primary" onClick={() => fetchIncome(page)} disabled={loading}><Icon name="refresh" size={16}/> Refresh History</button>
          </header>

          <section className="rmih-stats" aria-label="Income overview">
            <div className="rmih-stat"><div className="rmih-stat-head"><span className="rmih-stat-label">Total Records</span><span className="rmih-stat-icon"><Icon name="layers" size={17}/></span></div><div className="rmih-stat-value">{total.toLocaleString()}</div><p className="rmih-stat-foot">Across all pages</p></div>
            <div className="rmih-stat"><div className="rmih-stat-head"><span className="rmih-stat-label">Current Page</span><span className="rmih-stat-icon"><Icon name="calendar" size={17}/></span></div><div className="rmih-stat-value">{page} <span style={{fontSize:14,fontWeight:500}}>/ {lastPage}</span></div><p className="rmih-stat-foot">Server-side pagination</p></div>
            <div className="rmih-stat"><div className="rmih-stat-head"><span className="rmih-stat-label">Records on This Page</span><span className="rmih-stat-icon"><Icon name="chart" size={17}/></span></div><div className="rmih-stat-value">{income.length}</div><p className="rmih-stat-foot">Income entries loaded</p></div>
          </section>

          <section className="rmih-panel">
            <div className="rmih-panel-head">
              <h3 className="rmih-panel-title"><Icon name="chart" size={20}/> Income Transactions</h3>
              <div className="rmih-tools">
                <label className="rmih-search"><Icon name="search" size={17}/><input type="search" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search this page..." aria-label="Search current page income"/></label>
                <span className="rmih-page-label">Page {page}</span>
              </div>
            </div>
            {error && <div className="rmih-error" role="alert">{error}</div>}
            <div className="rmih-scroll">
              <table className="rmih-table">
                <thead><tr><th>{t("pro_IncHis_type")}</th><th>User Name</th><th>Trade Amount</th><th>Income Amount</th><th>{t("pro_IncHis_date")}</th></tr></thead>
                <tbody>
                  {filteredIncome.length ? filteredIncome.map((row,index) => (
                    <tr key={row.income_Id ?? `${page}-${index}`}>
                      <td><span className="rmih-type"><span className="rmih-type-icon"><Icon name={getIncomeIcon(row.income_Type)} size={17}/></span>{row.income_Type || "—"}</span></td>
                      <td><span className="rmih-amount">{row.to_user_name ?? "Null"}</span></td>
                      <td><span className="rmih-amount">{amountPrefix(row.income_Type)}{row.main_amount ?? "0"}</span></td>
                      <td><span className="rmih-amount">{amountPrefix(row.income_Type)}{row.income_Amount ?? "0"}</span></td>
                      <td><span className="rmih-date">{row.date || "—"}</span></td>
                    </tr>
                  )) : <tr><td colSpan={3}><div className="rmih-empty"><Icon name="layers" size={32}/>{loading ? "Fetching transactions..." : search ? "No matching records on this page" : "No income history found"}</div></td></tr>}
                </tbody>
              </table>
            </div>
            <footer className="rmih-footer">
              <div className="rmih-count">Showing {total ? (page-1)*perPage+1 : 0}–{Math.min(page*perPage,total)} of {total.toLocaleString()} records {loading ? "· Updating..." : ""}</div>
              <nav className="rmih-pages" aria-label="Income history pages">
                <button type="button" className="rmih-page" onClick={() => goPage(page-1)} disabled={loading || page===1} aria-label="Previous page"><span style={{transform:"rotate(180deg)",display:"inline-flex"}}><Icon name="chevron" size={16}/></span></button>
                {pages.map(n => <button type="button" key={n} className={`rmih-page${n===page ? " active" : ""}`} onClick={() => goPage(n)} disabled={loading} aria-current={n===page ? "page" : undefined}>{n}</button>)}
                <button type="button" className="rmih-page" onClick={() => goPage(page+1)} disabled={loading || page===lastPage} aria-label="Next page"><Icon name="chevron" size={16}/></button>
              </nav>
            </footer>
          </section>
        </div>
      </main>
    </>
  );
};

export default BasicDatatable;
